import React, { Component } from 'react';
import styled from 'styled-components';
import { HamburgerButton } from '@flow/components/navigation';
import { FlowLogo } from '@flow/components/logo/FlowLogo';
import { withSiteData, Link } from 'react-static';
import { RouteComponentProps, withRouter } from 'react-router';
import { isArray } from 'util';
import Headroom from 'react-headroom';
import { FormattedMessage } from 'react-intl';

const Wrapper = styled.div`
  position: relative;
  z-index: 999;
  .headroom {
    @media (min-width: ${props => props.theme.narrowTopBarLayout}px) {
      position: fixed !important;
      transform: translateY(0) !important;
    }
    background-color: white;
    top: 0;
    width: 100%;
    max-width: ${props => props.theme.containerMaxW/10}em;
    margin: auto;
    z-index: 999;
    overflow: hidden;
    border-bottom: 0.1em solid black;
    padding: 0 3em;
    box-sizing: border-box;
  }
`;

const BarContent = styled.div`
  position: relative;
  display: flex;
  height: ${props => props.theme.topBarHeight/10}em;
  align-items: center;
  justify-content: center;
`;

const HomeLinkWrapper = styled.div`
  flex: 1;
  display: block;
  text-align: center; 
  
  @media (max-width: ${props => props.theme.narrowTopBarLayout}px) {
    text-align: left;
  }
`;

const HamburgerWrapper = styled.div`
  flex: 0;
  min-width: 10em;
  
  @media (max-width: 690px) {
    min-width: auto;
  }
`;

const TicketsButton = styled.span` 
  &.tickets-button-wide-layout {
    @media (max-width: ${props => props.theme.narrowTopBarLayout}px) {
      display: none;
    }
  }
  display: block;
  a {
    box-sizing: border-box;
    border-radius: ${1.8/1.8}em;
    display: block;
    white-space: nowrap;
    font-family: Flow-Poster-Bold;
    text-transform: uppercase;
    font-size: 1.8em;
    padding: ${1.2/1.8}em;
    background-color: ${props => props.theme.highlightColor};  
  }
`;

const LanguageWrapper = styled.div`
  min-width: 10em;
`;

const Languages = styled.p`
  a {
    font-family: Flow-Text;
    text-transform: uppercase;
    font-size: 2.1em;
    display: inline-block;
    padding: ${(.5/2.1)}em;
    
    span {
      transition: color .2s ease-out;
    }
    
    &.active-lang span,
    &:${props => props.theme.hoverProperty} span {
      color: ${props => props.theme.highlightColor};
    }
    
    &:after {
      content: '/';
      display: inline-block;
      padding-left: .5em;
    }
    
    &:last-child {
      &:after {
        display: none;
      }
    }
  }
`;

const LanguageMenuBar = styled.div`
  @media (max-width: 870px) {
    display: none;
  }
`;

const NarrowLanguageMenuBar = styled.div`
  height: ${props => props.theme.narrowTopBarLanguageBarH / 10}em;
  display: flex;
  align-items: center;
  
  @media (min-width: 871px) {
    display: none;
  }
`;

interface Props {
  appLanguage: IAppLanguage;
  lang_versions: LangVersions
  navigation: any;
  settings: ISettings;
  languages: any;
  history: any;
}

class TopBar extends Component<Props & RouteComponentProps<{}>> {
  _renderLanguageNavItems = () => {

    const {
      languages,
      settings,
    } = this.props;

    const languageKeys = Object.keys(languages).sort((a, b) => { return (a > b ? 1 : -1); });
    const defaultLangCode = settings.languages['default'];
    const activeLang = this._parseActiveLang();

    return (
      <LanguageWrapper>
        <Languages>
          { languageKeys.map((lang, key) => {
            return (
              <Link
                key={key}
                to={this._getCorrectLangURL(languages[lang].code, defaultLangCode)}
                className={activeLang === languages[lang].code ? 'active-lang': ''}
              >
                <span>{ languages[lang].code }</span>
              </Link>
            )
          }) }
        </Languages>
      </LanguageWrapper>
    );
  };

  private _parseActiveLang = () => {
    const {
      history: {
        location: {
          pathname,
        }
      }
    } = this.props;

    if (pathname.match(/\/en\//) || (pathname.match(/\/en/) && pathname.length === 3)) {
      return 'en';
    } else if (pathname.match(/\/ru\//) || (pathname.match(/\/ru/) && pathname.length === 3)) {
      return 'ru';
    } else {
      return 'fi';
    }
  };

  render() {
    const ticketLink = (): string => {
      let link: string = null;
      switch(this.props.appLanguage.code) {
        case 'fi':
          link = '/liput';
          break;

        case 'en':
          link = '/tickets';
          break;

        case 'ru':
          link = '/bilety';
          break;
      }
      return link;
    }

    return (
      <Wrapper>
        <Headroom>
          <NarrowLanguageMenuBar>
            <div style={{display: 'flex', justifyContent: 'space-between', flexGrow: 1, alignItems: 'center'}}>
              {this._renderLanguageNavItems()}
              {ticketLink() &&
              <TicketsButton>
                  <Link to={ticketLink()}>
                    <FormattedMessage id={'ticketsLinkLabel'}/>
                  </Link>
              </TicketsButton>
              }
            </div>
          </NarrowLanguageMenuBar>
          <BarContent>
            <LanguageMenuBar>
              {this._renderLanguageNavItems()}
            </LanguageMenuBar>
            <HomeLinkWrapper>
              <FlowLogo appLanguage={this.props.appLanguage}/>
            </HomeLinkWrapper>
            {ticketLink() &&
            <TicketsButton className={'tickets-button-wide-layout'}>
                <Link to={ticketLink()}>
                  <FormattedMessage id={'ticketsLinkLabel'}/>
                </Link>
            </TicketsButton>
            }
            <HamburgerWrapper>
              <HamburgerButton/>
            </HamburgerWrapper>
          </BarContent>
        </Headroom>
      </Wrapper>
    )
  }

  private _getCorrectLangURL = (code: string, defaultLangCode: string) : string => {
    const {
      lang_versions,
    } = this.props;

    if (lang_versions && isArray(lang_versions)) {
      const lang_version: LangVersion = lang_versions.find((lang_version: LangVersion) => lang_version.lang_code === code);
      return lang_version.url;
    } else {
      return code === defaultLangCode ? '/' : `/${code}`;
    }
  }
}

export default withRouter(withSiteData(TopBar));



// WEBPACK FOOTER //
// /Users/ek1z/dev/flowfestival-com/src/components/navigation/TopBar.tsx